/**
 * Torah Scroll Animation Module
 *
 * Renders an animated Torah scroll that unrolls to reveal text.
 * Features:
 * - Two scroll handles with wooden texture gradient
 * - Parchment unrolling from center
 * - "Spirit of Shabbat" text revealed as scroll opens
 * - Subtle shadow beneath scroll
 */

import { LOADER_COLORS, type AnimationModule, type AnimationPhase } from '../types';
import { Easing, lerp, clamp } from './AnimationEngine';

interface ScrollState {
  unrollProgress: number;
  targetUnrollProgress: number;
  textOpacity: number;
  handleRotation: number;
  shadowBlur: number;
  exitScale: number;
  exitY: number;
}

export class TorahScrollAnimation implements AnimationModule {
  id = 'torah-scroll';

  private height = 0;

  // Scroll dimensions (will be calculated based on canvas size)
  private scrollWidth = 0;
  private scrollHeight = 0;
  private handleWidth = 0;
  private handleHeight = 0;
  private centerX = 0;
  private centerY = 0;

  // Animation state
  private state: ScrollState = {
    unrollProgress: 0,
    targetUnrollProgress: 1,
    textOpacity: 0,
    handleRotation: 0,
    shadowBlur: 10,
    exitScale: 1,
    exitY: 0,
  };

  // Timing
  private exitProgress = 0;

  init(_ctx: CanvasRenderingContext2D, width: number, height: number): void {
    this.resize(width, height);
  }

  resize(width: number, height: number): void {
    this.height = height;

    // Calculate scroll dimensions based on canvas size
    // Increased 25% for better mobile visibility
    const minDimension = Math.min(width, height);
    const scale = 0.75; // 25% larger than previous 0.6
    this.scrollWidth = minDimension * 0.5 * scale;
    this.scrollHeight = minDimension * 0.35 * scale;
    this.handleWidth = this.scrollWidth * 0.14;
    this.handleHeight = this.scrollHeight * 1.1;

    this.centerX = width / 2;
    this.centerY = height / 2 + 5; // Slightly below center to make room for Hebrew text
  }

  update(deltaTime: number, phase: AnimationPhase, progress: number): void {
    const dt = deltaTime / 1000; // Convert to seconds

    switch (phase) {
      case 'intro':
        // Unroll animation with easing
        this.state.targetUnrollProgress = Easing.easeOutCubic(progress);
        // Text fades in after scroll is mostly unrolled
        this.state.textOpacity = clamp((progress - 0.5) * 2, 0, 1);
        break;

      case 'loop': {
        // Gentle breathing animation
        const breathe = Math.sin(performance.now() / 1000) * 0.02;
        this.state.targetUnrollProgress = 1 + breathe;
        this.state.textOpacity = 1;
        break;
      }

      case 'exit':
        this.exitProgress = progress;
        // Scroll contracts and moves up
        this.state.exitScale = lerp(1, 0.3, Easing.easeInCubic(progress));
        this.state.exitY = lerp(0, -this.height * 0.4, Easing.easeInQuad(progress));
        this.state.textOpacity = lerp(1, 0, Easing.easeOutQuad(Math.min(progress * 2, 1)));
        break;

      case 'complete':
        this.state.exitScale = 0;
        break;
    }

    // Smooth interpolation of unroll progress
    const lerpSpeed = 5;
    this.state.unrollProgress = lerp(
      this.state.unrollProgress,
      this.state.targetUnrollProgress,
      clamp(dt * lerpSpeed, 0, 1)
    );

    // Handle rotation for visual interest
    this.state.handleRotation += dt * 0.5 * (1 - this.state.unrollProgress);
  }

  render(ctx: CanvasRenderingContext2D): void {
    ctx.save();

    // Apply exit transform
    if (this.exitProgress > 0) {
      ctx.translate(this.centerX, this.centerY);
      ctx.scale(this.state.exitScale, this.state.exitScale);
      ctx.translate(-this.centerX, -this.centerY + this.state.exitY);
    }

    // Draw shadow beneath scroll
    this.drawShadow(ctx);

    // Draw parchment (unrolled paper)
    this.drawParchment(ctx);

    // Draw scroll handles
    this.drawHandles(ctx);

    // Draw text on parchment
    this.drawText(ctx);

    ctx.restore();
  }

  private drawShadow(ctx: CanvasRenderingContext2D): void {
    const shadowY = this.centerY + this.scrollHeight * 0.55;
    const shadowWidth = this.scrollWidth * 0.8 * this.state.unrollProgress;
    const shadowHeight = 15;

    ctx.save();
    ctx.beginPath();
    ctx.ellipse(this.centerX, shadowY, shadowWidth / 2, shadowHeight / 2, 0, 0, Math.PI * 2);

    const gradient = ctx.createRadialGradient(
      this.centerX,
      shadowY,
      0,
      this.centerX,
      shadowY,
      shadowWidth / 2
    );
    gradient.addColorStop(0, 'rgba(99, 52, 94, 0.2)');
    gradient.addColorStop(1, 'rgba(99, 52, 94, 0)');

    ctx.fillStyle = gradient;
    ctx.fill();
    ctx.restore();
  }

  private drawParchment(ctx: CanvasRenderingContext2D): void {
    const unroll = this.state.unrollProgress;
    const parchmentWidth = this.scrollWidth * 0.7 * unroll;
    const parchmentHeight = this.scrollHeight * 0.85;

    const left = this.centerX - parchmentWidth / 2;
    const top = this.centerY - parchmentHeight / 2;

    ctx.save();

    // Parchment background with subtle gradient
    const gradient = ctx.createLinearGradient(
      left,
      top,
      left + parchmentWidth,
      top + parchmentHeight
    );
    gradient.addColorStop(0, LOADER_COLORS.parchment);
    gradient.addColorStop(0.5, LOADER_COLORS.goldPale);
    gradient.addColorStop(1, LOADER_COLORS.parchmentDark);

    ctx.fillStyle = gradient;
    ctx.beginPath();

    // Rounded rectangle for parchment
    const radius = 4;
    ctx.roundRect(left, top, parchmentWidth, parchmentHeight, radius);
    ctx.fill();

    // Subtle border
    ctx.strokeStyle = LOADER_COLORS.gold;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Decorative edge lines (like scroll creases)
    if (unroll > 0.3) {
      ctx.strokeStyle = `rgba(212, 165, 116, ${(unroll - 0.3) * 0.3})`;
      ctx.lineWidth = 0.5;

      // Left edge
      ctx.beginPath();
      ctx.moveTo(left + 3, top + 5);
      ctx.lineTo(left + 3, top + parchmentHeight - 5);
      ctx.stroke();

      // Right edge
      ctx.beginPath();
      ctx.moveTo(left + parchmentWidth - 3, top + 5);
      ctx.lineTo(left + parchmentWidth - 3, top + parchmentHeight - 5);
      ctx.stroke();
    }

    ctx.restore();
  }

  private drawHandles(ctx: CanvasRenderingContext2D): void {
    const unroll = this.state.unrollProgress;
    const parchmentWidth = this.scrollWidth * 0.7 * unroll;

    // Left handle
    const leftHandleX = this.centerX - parchmentWidth / 2 - this.handleWidth * 0.6;
    this.drawHandle(ctx, leftHandleX, this.centerY, false);

    // Right handle
    const rightHandleX = this.centerX + parchmentWidth / 2 + this.handleWidth * 0.6;
    this.drawHandle(ctx, rightHandleX, this.centerY, true);
  }

  private drawHandle(ctx: CanvasRenderingContext2D, x: number, y: number, _isRight: boolean): void {
    const w = this.handleWidth;
    const h = this.handleHeight;

    ctx.save();
    ctx.translate(x, y);

    // Main handle body - purple gradient to match theme
    const bodyGradient = ctx.createLinearGradient(-w / 2, 0, w / 2, 0);
    bodyGradient.addColorStop(0, LOADER_COLORS.primaryDeep);
    bodyGradient.addColorStop(0.3, LOADER_COLORS.scrollHandleLight);
    bodyGradient.addColorStop(0.7, LOADER_COLORS.scrollHandle);
    bodyGradient.addColorStop(1, LOADER_COLORS.primaryDeep);

    ctx.fillStyle = bodyGradient;
    ctx.beginPath();
    ctx.roundRect(-w / 2, -h / 2, w, h, 3);
    ctx.fill();

    // Handle outline
    ctx.strokeStyle = LOADER_COLORS.primaryDeep;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Top ornament (finial)
    this.drawFinial(ctx, 0, -h / 2 - 6);

    // Bottom ornament (finial)
    this.drawFinial(ctx, 0, h / 2 + 6);

    // Decorative bands on handle - gold accents
    ctx.fillStyle = LOADER_COLORS.goldBright;
    ctx.beginPath();
    ctx.roundRect(-w / 2 + 2, -h / 4, w - 4, 2, 1);
    ctx.fill();
    ctx.beginPath();
    ctx.roundRect(-w / 2 + 2, h / 4 - 2, w - 4, 2, 1);
    ctx.fill();

    ctx.restore();
  }

  private drawFinial(ctx: CanvasRenderingContext2D, x: number, y: number): void {
    const radius = this.handleWidth * 0.5;

    // Finial (decorative top/bottom of scroll handles) - purple theme
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
    gradient.addColorStop(0, LOADER_COLORS.primaryLight);
    gradient.addColorStop(0.5, LOADER_COLORS.scrollHandle);
    gradient.addColorStop(1, LOADER_COLORS.primaryDeep);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = LOADER_COLORS.primaryDeep;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Inner decoration - gold accent
    ctx.fillStyle = LOADER_COLORS.goldBright;
    ctx.beginPath();
    ctx.arc(x, y, radius * 0.35, 0, Math.PI * 2);
    ctx.fill();
  }

  private drawText(ctx: CanvasRenderingContext2D): void {
    if (this.state.textOpacity <= 0) return;

    const unroll = this.state.unrollProgress;
    if (unroll < 0.5) return;

    ctx.save();
    ctx.globalAlpha = this.state.textOpacity * Easing.easeOutQuad((unroll - 0.5) * 2);

    // "Spirit of Shabbat" text on the scroll
    ctx.font = `${this.scrollHeight * 0.12}px Georgia, serif`;
    ctx.fillStyle = LOADER_COLORS.primary;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Main title
    ctx.fillText('Spirit of Shabbat', this.centerX, this.centerY - this.scrollHeight * 0.1);

    // Decorative underline
    const textWidth = ctx.measureText('Spirit of Shabbat').width;
    ctx.strokeStyle = LOADER_COLORS.gold;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(this.centerX - textWidth * 0.4, this.centerY + this.scrollHeight * 0.02);
    ctx.lineTo(this.centerX + textWidth * 0.4, this.centerY + this.scrollHeight * 0.02);
    ctx.stroke();

    // Subtitle
    ctx.font = `${this.scrollHeight * 0.07}px Georgia, serif`;
    ctx.fillStyle = LOADER_COLORS.primaryLight;
    ctx.fillText('Torah Study Companion', this.centerX, this.centerY + this.scrollHeight * 0.15);

    ctx.restore();
  }

  triggerExit(): void {
    this.exitProgress = 0;
  }

  destroy(): void {
    // Cleanup if needed
  }
}
